import type { UseQueryResult } from '@tanstack/react-query'
import type { CallEntry } from '../../api/adapters/callHistory'
import { userFacingMessage } from '../../api/errors'
import { Skeleton } from '../../ui/Feedback'
import { formatDate } from '../../lib/format'
import { Prose } from './Prose'
import { SignalEvidence } from './SignalEvidence'
import styles from './DealPanel.module.css'

/**
 * The calls behind the deal, newest first.
 *
 * Each entry is a point-in-time record and is rendered as one: the signals shown
 * are the signals confirmed ON THAT CALL, never the deal's accumulated state.
 *
 * **No per-call verdict.** Entries used to carry "Recommendation at the time",
 * which read "not recorded" on most rows because the backend only began storing
 * a canonical value part-way through. A field that is usually blank teaches the
 * reader to skip the line, and when it was populated it invited a comparison
 * against a current recommendation computed from all calls — not a like-for-like
 * one. What the user needs from history is what each call established and what
 * they themselves decided; the verdict that matters is the current one, shown
 * once at the top (audit D4/V3).
 */
export function CallHistory({ query }: { query: UseQueryResult<CallEntry[], Error> }) {
  return (
    <section>
      <h3 className={styles.section}>Call history</h3>

      {query.isPending ? (
        <div className={styles.loading}>
          <Skeleton height={70} />
          <Skeleton height={70} />
        </div>
      ) : query.isError ? (
        <p className={styles.failed} role="alert">
          Call history could not be loaded. {userFacingMessage(query.error)}
        </p>
      ) : query.data.length === 0 ? (
        <p className={styles.absent}>No calls analysed yet.</p>
      ) : (
        <ol className={styles.calls}>
          {query.data.map((call, index) => (
            <li key={call.id} className={styles.call}>
              <header className={styles.callHead}>
                <span className={styles.callTitle}>
                  Call {call.callNumber}
                  {index === 0 ? ' (latest)' : ''}
                </span>
                <span className={styles.callDate}>{formatDate(call.callDate)}</span>
              </header>

              {call.summary ? (
                <div className={styles.callSummary}>
                  <Prose text={call.summary} />
                </div>
              ) : null}

              <SignalEvidence
                confirmed={call.confirmedSignals}
                missing={call.missingSignals}
                label="What this call established"
              />

              {call.confirmedRisks.length > 0 ? (
                <p className={styles.meta}>Risks on this call: {call.confirmedRisks.join(', ')}</p>
              ) : null}
              {call.blockerCount > 0 ? (
                <p className={styles.meta}>
                  {call.blockerCount} blocker{call.blockerCount === 1 ? '' : 's'} recorded on this call
                </p>
              ) : null}

              {/* Only a decision that was actually made. "No decision recorded
                  for this call" was a line on most entries that told the reader
                  nothing they could act on. */}
              {call.decision ? (
                <p className={styles.callDecision}>
                  You decided: {call.decision.label}
                  {call.decision.reason ? ` — ${call.decision.reason}` : ''}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
